import {
  Autocomplete,
  Box,
  Checkbox,
  Fade,
  FormControlLabel,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import {
  Label,
  Labels,
  Loader,
  RejectionProbabilityInfo,
  TableRejectionRates
} from 'components'
import { QUERIES, SnackbarUtil } from 'helpers'
import React from 'react'
import { useQuery } from 'react-query'
import { useHistory } from 'react-router-dom'
import { api, apiConfig, useMetricsContext } from 'services'
import { useGetLabels } from '../../../hooks/useGetLabels'

const initialPageSize = 100

export const listAllInstituteLines = async (filters) => {
  const params = {
    ...filters,
    page: 0,
    size: initialPageSize
  }
  const firstPage = await api.lines.list(params)
  const firstPageItems = firstPage.items || []

  if (firstPageItems.length >= firstPage.total) return firstPage

  return api.lines.list({
    ...params,
    size: firstPage.total
  })
}

const TableView = () => {
  const history = useHistory()
  const { metricType, metricLabel } = useMetricsContext()
  const [filters, setFilters] = React.useState({
    archived: false
  })
  const [nameSortDirection, setNameSortDirection] = React.useState(null)

  const { data: getLabelsData, isLoading: isGetLabelsDataIsLoading } =
    useGetLabels()
  const { data: lineData, isLoading: isLineDataLoading } = useQuery({
    queryKey: [
      QUERIES.GET_ALL_LINES,
      'all',
      apiConfig.currentApiInstitute,
      filters
    ],
    queryFn: () => listAllInstituteLines(filters),
    staleTime: Infinity,
    onError: () => {
      SnackbarUtil.error('Error fetching data', {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      })
    }
  })

  const lines = React.useMemo(() => {
    const items = lineData?.items || []
    if (!nameSortDirection) return items

    return [...items].sort((left, right) => {
      const comparison = (left.name || '').localeCompare(right.name || '')
      return nameSortDirection === 'desc' ? -comparison : comparison
    })
  }, [lineData?.items, nameSortDirection])

  const renderLineLength = (length) => {
    if (length === null || length === undefined || length === '') {
      return <Box sx={{ color: 'text.secondary' }}>-</Box>
    }

    return `${length} m`
  }

  const handleLabelFilterChange = async (event, labels) => {
    if (labels.length === 0) {
      const { labels, ...rest } = filters
      setFilters(rest)
      return
    }

    setFilters({
      ...filters,
      labels: labels.map((label) => label.id).join(',')
    })
  }

  const handleShowArchivedChange = (event) => {
    setFilters({
      ...filters,
      archived: event.target.checked
    })
  }

  const handleNameSort = () => {
    setNameSortDirection((currentDirection) => {
      if (currentDirection === 'asc') return 'desc'
      if (currentDirection === 'desc') return null
      return 'asc'
    })
  }

  return (
    <Fade in timeout={500}>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Box display={'flex'} paddingTop={2} gap={2}>
          <TextField
            label={'Line Name'}
            size={'small'}
            variant={'outlined'}
            onChange={(event) => {
              setFilters({
                ...filters,
                name__icontains: event.target.value
              })
            }}
          />
          <Autocomplete
            multiple
            size={'small'}
            options={isGetLabelsDataIsLoading ? [] : getLabelsData || []}
            getOptionLabel={(option) => option?.label || ''}
            onChange={handleLabelFilterChange}
            filterSelectedOptions={true}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                return (
                  <Label
                    key={`label-option-${option?.id}-${index}`}
                    label={option?.label}
                    color={option?.color}
                    size="small"
                    {...getTagProps({ index })}
                  />
                )
              })
            }
            renderOption={(props, option) => (
              <li {...props}>
                <Label
                  key={`label-option-${option?.id}`}
                  label={option?.label}
                  color={option?.color}
                  size="small"
                />
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Labels" />}
            sx={{
              flexGrow: 1
            }}
          />
          <FormControlLabel
            control={<Checkbox onChange={handleShowArchivedChange} />}
            label={'Show Archived'}
          />
        </Box>

        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead
              sx={{ '& .MuiTableCell-head': { borderBottom: 1, borderColor: 'divider' } }}
            >
              <TableRow>
                <TableCell sx={{ width: '18%' }}>
                  <TableSortLabel
                    active={Boolean(nameSortDirection)}
                    direction={nameSortDirection || 'asc'}
                    onClick={handleNameSort}
                  >
                    {'Name'}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  {'Labels'}
                </TableCell>
                <TableCell sx={{ width: '17%' }}>
                  {'Record Count'}
                </TableCell>
                <TableCell sx={{ width: '17%' }}>
                  {metricLabel}
                </TableCell>
                <TableCell sx={{ width: '12%' }}>
                  {'Length (m)'}
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  <Box display="flex" flexDirection="row" alignItems="center">
                    {'Rejection Probability'}
                    <Box display="flex" pl={1}>
                      <RejectionProbabilityInfo />
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLineDataLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Loader title="Retrieving Data..." message={null} />
                  </TableCell>
                </TableRow>
              ) : lines.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="h5">{'No Rows Found'}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lines.map((line) => {
                  const lineStats = line.validation || line.line_statistics
                  const metricStats = lineStats?.[metricType]
                  const labels = line.labels || []

                  return (
                    <TableRow key={line.id}>
                      <TableCell>
                        <Tooltip
                          title={line.name || ''}
                          placement="right-end"
                        >
                          <Link
                            underline="none"
                            fontSize={18}
                            fontWeight={600}
                            onClick={() => history.push(`/line/${line.id}`)}
                            sx={{
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {line.name || ''}
                          </Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Labels
                          shownLabels={labels.slice(0, 1)}
                          hiddenLabels={labels.slice(1)}
                          display="flex"
                          flexDirection="column"
                          justifyContent="flex-start"
                        />
                      </TableCell>
                      <TableCell>
                        {line.line_statistics
                          ? line.line_statistics.record_count
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {metricStats ? metricStats.mean : '-'}
                      </TableCell>
                      <TableCell>{renderLineLength(line.length)}</TableCell>
                      <TableCell>
                        {metricStats?.rejection_rates ? (
                          <TableRejectionRates
                            rejectionRates={metricStats.rejection_rates}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isLineDataLoading && (
          <Box display="flex" justifyContent="flex-end" py={1}>
            <Typography variant="body2">{`Rows: ${lines.length}`}</Typography>
          </Box>
        )}
      </Box>
    </Fade>
  )
}

export default TableView
